/** @internal */
export type EnumValue<E extends string | number> = E | `${E}`

/**
 * Returns explicit enum and associated ns keys with string|number value.
 *
 * @example
 * ```typescript
 * enum MyEnum { FOO = 'foo' }
 * getEnumKeys(MyEnum) -> ['FOO']
 * ```
 *
 * @internal
 */
export function getEnumKeys<Enum extends Record<string, any>>(enu: Enum): string[] {
    return Object.keys(enu).filter(
        (key) =>
            (isNaN(Number(key)) || /^0\d+/.test(key)) && (typeof enu[key] === 'string' || typeof enu[key] === 'number'),
    )
}

/**
 * Returns values for all keys returned in {@link getEnumKeys}.
 *
 * @example
 * ```typescript
 * enum MyEnum { FOO = 'foo' }
 * getEnumValues(MyEnum) -> ['foo']
 * ```
 *
 * @internal
 */
export function getEnumValues<ValueType>(enu: Record<string, ValueType>): ValueType[] {
    return getEnumKeys(enu).map((key) => enu[key])
}

/**
 * Returns a copy of the provided enum with all non-key members removed.
 *
 * Keys determined by {@link getEnumKeys}.
 *
 * @example
 * ```typescript
 * enum MyEnum { FOO = 'foo' }
 * namespace MyEnum { parse() { ... } }
 *
 * MyEnum.parse('foo') -> MyEnum.FOO
 *
 * const PureEnum = getPureEnum(MyEnum)
 * PureEnum.parse('foo') -> throws TypeError
 *
 * ```
 *
 * @internal
 */
export function getPureEnum<Enum extends Record<string, any>>(enu: Enum): Enum {
    return Object.fromEntries(getEnumKeys(enu).map((key) => [key, enu[key]])) as Enum
}

/**
 * Ensures string|number value return types only
 *
 * @internal
 */
type EnumValueType<T> = T extends (...args: any[]) => any
    ? never
    : T extends object
    ? never
    : T extends Array<any>
    ? never
    : T extends Symbol
    ? never
    : T extends boolean
    ? never
    : T extends T
    ? T
    : never

/**
 * Attempts to parse an enum member from a string value via the enum member value.
 * If no matching member is found an Error is thrown.
 *
 * @throws Error
 *
 * @example
 * ```typescript
 * enum MyEnum { FOO = 'foo' }
 *
 * stringToEnum('foo') -> MyEnum.FOO
 * stringToEnum('FOO') -> throws Error
 * stringToEnum('bar') -> throws Error
 * ```
 *
 * @internal
 */
export function enumFromValue<T>(enu: Record<string, T>, val: string | number | T): EnumValueType<T> {
    const parsed = (getEnumValues(enu).includes(val as T) ? val : undefined) as T | undefined
    if (parsed === undefined) {
        throw new Error(
            `Invalid enum member ${typeof val === 'string' ? `"${val}"` : val}. ${JSON.stringify(getPureEnum(enu))}`,
        )
    }
    return parsed as EnumValueType<T>
}

/** @internal */
export function tryEnumFromValue<T>(enu: { [key: string]: T }, val: string | number | T): EnumValueType<T> | undefined {
    try {
        return enumFromValue(enu, val)
    } catch {}
}
